<template>
  <v-container fluid class="px-4 py-4">
    <!-- Sync overlay -->
    <v-overlay :value="syncing" z-index="200" opacity="0.75">
      <v-card width="460" class="pa-8" rounded="lg" elevation="12">
        <div class="text-center">
          <v-avatar color="primary lighten-5" size="72" class="mb-4">
            <v-icon size="38" color="primary">mdi-database-sync</v-icon>
          </v-avatar>
          <h2 class="text-h6 font-weight-bold mb-1">Syncing Orders</h2>
          <p class="text-body-2 grey--text text--darken-1 mb-5">
            Importing all orders from WooCommerce into the local database
          </p>

          <div class="mb-2 d-flex justify-space-between">
            <span class="text-caption grey--text">Progress</span>
            <span class="text-caption font-weight-bold primary--text"
              >{{ syncProgress.percent }}%</span
            >
          </div>
          <v-progress-linear
            :value="syncProgress.percent"
            height="10"
            rounded
            color="primary"
            background-color="grey lighten-3"
            class="mb-4"
          />

          <v-row no-gutters class="mb-5">
            <v-col cols="4">
              <div class="text-h6 font-weight-bold">
                {{ syncProgress.synced.toLocaleString() }}
              </div>
              <div class="text-caption grey--text">Synced</div>
            </v-col>
            <v-col cols="4">
              <div class="text-h6 font-weight-bold">
                {{ syncProgress.totalOrders.toLocaleString() }}
              </div>
              <div class="text-caption grey--text">Total Orders</div>
            </v-col>
            <v-col cols="4">
              <div class="text-h6 font-weight-bold">
                {{ syncProgress.page }} / {{ syncProgress.totalPages }}
              </div>
              <div class="text-caption grey--text">Pages</div>
            </v-col>
          </v-row>

          <v-btn depressed color="error" small @click="cancelSync">
            <v-icon left small>mdi-close</v-icon>
            Cancel Sync
          </v-btn>
        </div>
      </v-card>
    </v-overlay>

    <!-- Page header -->
    <div class="d-flex flex-wrap align-center justify-space-between mb-5">
      <div>
        <h1 class="text-h5 font-weight-black mb-0">External Order Tracker</h1>
        <p class="text-body-2 grey--text text--darken-1 mb-0">
          Orders with SKU prefixes:
          <span v-for="p in skuPrefixes" :key="p">
            <v-chip x-small color="primary" dark class="mx-1">{{ p }}</v-chip>
          </span>
        </p>
      </div>
      <div class="d-flex align-center mt-2 mt-sm-0" style="gap: 10px">
        <v-btn
          outlined
          color="primary"
          small
          @click="runBulkSync"
          :disabled="syncing"
        >
          <v-icon left small>mdi-database-sync</v-icon>
          {{ syncStatus.totalSynced ? "Re-sync" : "Initial Sync" }}
        </v-btn>
        <v-btn
          depressed
          color="primary"
          @click="refreshOrders"
          :loading="loading"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </div>
    </div>

    <!-- Stats row -->
    <v-row class="mb-4" dense>
      <v-col cols="12" sm="4">
        <v-card
          outlined
          rounded
          class="pa-4 d-flex align-center"
          style="gap: 14px"
        >
          <v-avatar color="blue lighten-5" size="44">
            <v-icon color="blue darken-1">mdi-database-check</v-icon>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold">
              {{ syncStatus.totalSynced.toLocaleString() }}
            </div>
            <div class="text-caption grey--text text--darken-1">
              Total orders in DB
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card
          outlined
          rounded
          class="pa-4 d-flex align-center"
          style="gap: 14px"
        >
          <v-avatar color="green lighten-5" size="44">
            <v-icon color="green darken-1">mdi-tag-multiple</v-icon>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold">
              {{ syncStatus.externalCount.toLocaleString() }}
            </div>
            <div class="text-caption grey--text text--darken-1">
              External SKU orders
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card
          outlined
          rounded
          class="pa-4 d-flex align-center"
          style="gap: 14px"
        >
          <v-avatar color="orange lighten-5" size="44">
            <v-icon color="orange darken-1">mdi-clock-outline</v-icon>
          </v-avatar>
          <div>
            <div class="text-subtitle-2 font-weight-bold">
              {{
                syncStatus.lastSyncedAt
                  ? formatDate(syncStatus.lastSyncedAt)
                  : "—"
              }}
            </div>
            <div class="text-caption grey--text text--darken-1">
              Last synced
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Alerts -->
    <v-alert
      v-if="errorMessage"
      type="error"
      dense
      text
      class="mb-4"
      dismissible
      @input="errorMessage = ''"
    >
      {{ errorMessage }}
    </v-alert>
    <v-alert
      v-if="syncMessage"
      type="success"
      dense
      text
      class="mb-4"
      dismissible
      @input="syncMessage = ''"
    >
      {{ syncMessage }}
    </v-alert>

    <!-- Filter bar -->
    <v-card outlined rounded class="mb-4">
      <v-card-text class="py-3">
        <div class="d-flex flex-wrap align-center" style="gap: 12px">
          <v-text-field
            v-model="searchInput"
            placeholder="Search by order ID, customer or SKU…"
            prepend-inner-icon="mdi-magnify"
            hide-details
            dense
            outlined
            clearable
            style="max-width: 340px"
            @keyup.enter="onSearchEnter"
            @click:clear="onSearchClear"
          />
          <v-select
            v-model="statusFilter"
            :items="statusOptions"
            placeholder="All statuses"
            hide-details
            dense
            outlined
            clearable
            style="max-width: 200px"
          />
          <v-select
            v-model="skuFilter"
            :items="skuPrefixes"
            placeholder="All SKU prefixes"
            hide-details
            dense
            outlined
            multiple
            clearable
            style="max-width: 280px"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip
                x-small
                color="primary"
                dark
                class="mr-1"
                v-if="index < 2"
              >
                {{ item }}
              </v-chip>
              <span v-if="index === 2" class="text-caption grey--text">
                +{{ skuFilter.length - 2 }} more
              </span>
            </template>
          </v-select>
          <v-btn
            depressed
            color="primary"
            @click="applyFilters"
            :loading="loading"
          >
            <v-icon left small>mdi-magnify</v-icon>
            Search
          </v-btn>
          <v-btn
            depressed
            color="green darken-1"
            dark
            @click="exportToExcel"
            :loading="exporting"
          >
            <v-icon left small>mdi-microsoft-excel</v-icon>
            Export
          </v-btn>
          <v-spacer />
          <div class="text-body-2 grey--text text--darken-2">
            <strong>{{ totalOrders.toLocaleString() }}</strong> matching orders
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Table -->
    <v-card outlined rounded>
      <v-data-table
        :headers="headers"
        :items="orders"
        :loading="loading"
        loading-text="Loading orders…"
        no-data-text="No external SKU orders found"
        hide-default-footer
        dense
        class="orders-table"
      >
        <template v-slot:item.order_id="{ item }">
          <span class="font-weight-bold">#{{ item.order_id }}</span>
        </template>

        <template v-slot:item.order_date="{ item }">
          <span class="text-body-2">{{ item.order_date }}</span>
        </template>

        <template v-slot:item.customer="{ item }">
          <div>
            <div class="text-caption font-weight-medium">
              {{ item.customer }}
            </div>
            <div class="text-caption grey--text">{{ item.phone }}</div>
          </div>
        </template>

        <template v-slot:item.phone="{ item }">
          <span class="text-body-2">{{ item.phone }}</span>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip
            x-small
            :color="statusColor(item.status).bg"
            :text-color="statusColor(item.status).text"
            class="font-weight-medium text-capitalize"
          >
            {{ item.status }}
          </v-chip>
        </template>

        <template v-slot:item.total="{ item }">
          <span class="font-weight-medium">₹{{ item.total }}</span>
        </template>

        <template v-slot:item.matched_items="{ item }">
          <div class="d-flex flex-column" style="gap: 4px">
            <div
              v-for="line in item.matched_items"
              :key="line.id"
              class="d-flex flex-column mb-1"
            >
              <span class="text-caption font-weight-medium">
                {{ line.name }}
              </span>
              <div v-if="line.sku && line.sku.startsWith('CRCJAPMX2')" class="d-flex align-center flex-wrap mt-1" style="gap: 4px">
                <v-chip v-if="getLineMeta(line, '_crc_jm_source_item_id')" x-small color="blue" outlined>
                  Copy ID: {{ getLineMeta(line, '_crc_jm_source_item_id') }}
                </v-chip>
                <v-btn v-if="getLineMeta(line, '_crc_jm_album_url')" x-small depressed color="blue lighten-5" class="blue--text text--darken-3" :href="getLineMeta(line, '_crc_jm_album_url')" target="_blank" rel="noopener" style="text-transform: none; padding: 0 4px; height: 20px;">
                  <v-icon x-small left class="mr-1">mdi-open-in-new</v-icon>View on MION
                </v-btn>
              </div>
            </div>
          </div>
        </template>

        <template v-slot:item.matched_skus="{ item }">
          <div class="d-flex flex-wrap" style="gap: 4px">
            <div
              v-for="sku in item.matched_skus"
              :key="sku"
              class="d-flex align-center"
              style="gap: 2px"
            >
              <v-chip
                small
                color="primary"
                outlined
                class="font-weight-medium"
              >
                {{ sku }}
              </v-chip>
              <v-btn
                icon
                x-small
                :color="copiedSku === sku ? 'success' : 'grey'"
                @click="copySku(sku)"
                title="Copy SKU"
              >
                <v-icon x-small>{{ copiedSku === sku ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
              </v-btn>
              <v-btn
                v-if="sku.startsWith('CRCHAH')"
                icon
                x-small
                color="grey"
                :href="harmonieUrl(sku)"
                target="_blank"
                rel="noopener"
                title="Open Harmonie product page"
              >
                <v-icon x-small>mdi-open-in-new</v-icon>
              </v-btn>
            </div>
          </div>
        </template>

        <template v-slot:item.supplier_status="{ item }">
          <div class="d-flex flex-column py-2" style="gap: 10px">
            <div
              v-for="group in getVendorGroups(item)"
              :key="group.prefix"
              class="vendor-group-card"
              :style="{
                borderLeft: '4px solid ' + vendorBorderColor(group.status),
                background: vendorBgColor(group.status),
              }"
            >
              <!-- Header: prefix + status -->
              <div class="d-flex align-center justify-space-between" style="gap: 8px">
                <div class="d-flex align-center" style="gap: 6px">
                  <span class="vendor-prefix-label">{{ group.prefix }}</span>
                  <v-icon x-small :color="vendorStatusIconColor(group.status)">{{ vendorStatusIcon(group.status) }}</v-icon>
                  <span class="vendor-status-label" :style="{ color: vendorStatusIconColor(group.status) }">
                    {{ group.isHandDelivery && group.status === 'shipped_to_customer' ? 'Hand Delivered' : supplierStatusLabel(group.status) }}
                  </span>
                </div>
                <div class="d-flex align-center" style="gap: 2px">
                  <v-tooltip v-if="group.status !== 'processing'" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        color="grey"
                        v-bind="attrs"
                        v-on="on"
                        :loading="item._statusLoading === group.prefix"
                        @click="updateVendorStatus(item, group.prefix, vendorPrevStatus(group.status))"
                      >
                        <v-icon x-small>mdi-undo-variant</v-icon>
                      </v-btn>
                    </template>
                    <span>Step back one status</span>
                  </v-tooltip>
                  <v-tooltip v-if="group.status !== 'processing'" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        color="error"
                        v-bind="attrs"
                        v-on="on"
                        :loading="item._statusLoading === group.prefix"
                        @click="resetVendorPipeline(item, group.prefix)"
                      >
                        <v-icon x-small>mdi-restore</v-icon>
                      </v-btn>
                    </template>
                    <span>Reset import ship status</span>
                  </v-tooltip>
                </div>
              </div>

              <!-- Actions row -->
              <div class="d-flex align-center flex-wrap mt-2" style="gap: 6px">
                <!-- processing → Place Order -->
                <v-btn
                  v-if="group.status === 'processing'"
                  x-small
                  rounded
                  depressed
                  color="blue darken-1"
                  dark
                  class="px-3 text-none font-weight-medium"
                  :loading="item._statusLoading === group.prefix"
                  @click="updateVendorStatus(item, group.prefix, 'vendor_placed')"
                >
                  <v-icon left x-small>mdi-cart-arrow-right</v-icon>
                  Place Order
                </v-btn>

                <!-- vendor_placed → Mark Received + Tracking -->
                <template v-if="group.status === 'vendor_placed'">
                  <v-btn
                    x-small
                    rounded
                    depressed
                    color="purple darken-1"
                    dark
                    class="px-3 text-none font-weight-medium"
                    :loading="item._statusLoading === group.prefix"
                    @click="updateVendorStatus(item, group.prefix, 'vendor_received')"
                  >
                    <v-icon left x-small>mdi-package-down</v-icon>
                    Mark Received
                  </v-btn>
                  <v-btn
                    x-small
                    rounded
                    depressed
                    :color="hasTrackingDataForPrefix(item, group.prefix) ? 'orange darken-1' : 'grey lighten-1'"
                    :dark="hasTrackingDataForPrefix(item, group.prefix)"
                    class="px-3 text-none"
                    @click="openTrackingDialog(item, group.prefix)"
                  >
                    <v-icon left x-small>{{ group.isHandDelivery ? 'mdi-note-text-outline' : 'mdi-truck-outline' }}</v-icon>
                    {{ group.isHandDelivery ? 'Notes' : 'Tracking' }}
                  </v-btn>
                </template>

                <!-- vendor_received → Ship to Customer / Hand Delivered + Tracking -->
                <template v-if="group.status === 'vendor_received'">
                  <v-btn
                    v-if="!group.isHandDelivery"
                    x-small
                    rounded
                    depressed
                    color="green darken-1"
                    dark
                    class="px-3 text-none font-weight-medium"
                    @click="openCustomerShippingDialog(item, group.prefix, { mode: 'ship' })"
                  >
                    <v-icon left x-small>mdi-truck-fast-outline</v-icon>
                    Ship with Tracking
                  </v-btn>
                  <v-btn
                    v-if="!group.isHandDelivery"
                    x-small
                    rounded
                    outlined
                    color="green darken-1"
                    class="px-3 text-none"
                    :loading="item._statusLoading === group.prefix"
                    @click="markCustomerShippedWithoutTracking(item, group.prefix)"
                  >
                    Mark Shipped
                  </v-btn>
                  <v-btn
                    v-if="group.isHandDelivery"
                    x-small
                    rounded
                    depressed
                    color="green darken-1"
                    dark
                    class="px-3 text-none font-weight-medium"
                    :loading="item._statusLoading === group.prefix"
                    @click="updateVendorStatus(item, group.prefix, 'shipped_to_customer')"
                  >
                    <v-icon left x-small>mdi-hand-extended-outline</v-icon>
                    Hand Delivered
                  </v-btn>
                  <v-btn
                    x-small
                    rounded
                    depressed
                    :color="hasTrackingDataForPrefix(item, group.prefix) ? 'orange darken-1' : 'grey lighten-1'"
                    :dark="hasTrackingDataForPrefix(item, group.prefix)"
                    class="px-3 text-none"
                    @click="openTrackingDialog(item, group.prefix)"
                  >
                    <v-icon left x-small>{{ group.isHandDelivery ? 'mdi-note-text-outline' : 'mdi-truck-outline' }}</v-icon>
                    {{ group.isHandDelivery ? 'Notes' : 'Tracking' }}
                  </v-btn>
                </template>

                <!-- shipped_to_customer → Shipment details + Add Package -->
                <template v-if="group.status === 'shipped_to_customer'">
                  <div
                    v-if="!group.isHandDelivery && getCustomerShipmentsForPrefix(item, group.prefix).length === 0"
                    class="text-caption grey--text text--darken-1 mb-1"
                  >
                    Shipped — no tracking saved yet
                  </div>
                  <!-- Customer Shipment Cards -->
                  <div class="w-100" v-if="getCustomerShipmentsForPrefix(item, group.prefix).length > 0">
                    <div
                      v-for="(shipment, idx) in getCustomerShipmentsForPrefix(item, group.prefix)"
                      :key="'cs-'+idx"
                      class="shipment-row d-flex align-center pa-1 rounded flex-wrap"
                      style="gap: 6px; background: rgba(255,255,255,0.7);"
                    >
                      <div
                        class="d-flex align-center flex-grow-1"
                        style="gap: 6px; cursor: pointer; min-width: 0"
                        @click="openShipmentDetail(shipment, item)"
                      >
                        <v-icon x-small color="green darken-2">mdi-package-variant-closed</v-icon>
                        <span class="text-caption font-weight-medium" style="color:#555">{{ shipment.shipping_company }}</span>
                        <span class="text-caption" style="color:#999">·</span>
                        <span class="text-caption font-weight-bold" style="color:#333">{{ shipment.tracking_id }}</span>
                        <v-icon x-small color="grey lighten-1" class="ml-auto">mdi-chevron-right</v-icon>
                      </div>
                      <v-btn
                        v-if="customerShipWhatsAppMessage(item, shipment)"
                        x-small
                        rounded
                        depressed
                        color="#25D366"
                        dark
                        class="px-2 text-none"
                        @click.stop="copyCustomerShipWhatsApp(item, shipment)"
                      >
                        <v-icon left x-small>mdi-whatsapp</v-icon>
                        Copy WhatsApp
                      </v-btn>
                    </div>
                  </div>
                  <v-btn
                    v-if="!group.isHandDelivery"
                    x-small
                    rounded
                    outlined
                    color="green darken-1"
                    class="px-3 text-none"
                    @click="openCustomerShippingDialog(item, group.prefix, { mode: 'add' })"
                  >
                    <v-icon left x-small>mdi-plus</v-icon>
                    Add Package
                  </v-btn>
                  <v-btn
                    x-small
                    rounded
                    depressed
                    color="orange darken-1"
                    dark
                    class="px-3 text-none"
                    @click="openTrackingDialog(item, group.prefix)"
                  >
                    <v-icon left x-small>{{ group.isHandDelivery ? 'mdi-note-text-outline' : 'mdi-truck-check-outline' }}</v-icon>
                    {{ group.isHandDelivery ? 'Notes' : 'Vendor Tracking' }}
                  </v-btn>
                </template>
              </div>
            </div>
          </div>
        </template>
      </v-data-table>

      <!-- Pagination -->
      <v-divider v-if="totalPages > 1" />
      <div
        class="d-flex align-center justify-space-between px-4 py-3"
        v-if="totalPages > 1"
      >
        <span class="text-caption grey--text">
          Page {{ page }} of {{ totalPages }}
        </span>
        <v-pagination
          v-model="page"
          :length="totalPages"
          :total-visible="7"
          @input="onPageChange"
        />
      </div>
    </v-card>

    <!-- Vendor Tracking Dialog -->
    <v-dialog v-model="trackingDialog" max-width="640" persistent>
      <v-card v-if="trackingOrder">
        <v-card-title class="d-flex align-center justify-space-between pb-2">
          <div>
            <span class="text-h6 font-weight-bold">
              {{ trackingDialogPrefix && isHandDeliveryPrefix(trackingDialogPrefix) ? 'Delivery Notes' : 'Vendor Tracking' }}
            </span>
            <span class="ml-2 text-body-2 grey--text"
              >#{{ trackingOrder.order_id }} —
              {{ trackingOrder.customer }}</span
            >
            <v-chip
              v-if="trackingDialogPrefix"
              x-small
              color="primary"
              dark
              class="ml-2"
            >{{ trackingDialogPrefix }}</v-chip>
          </div>
          <v-btn icon @click="trackingDialog = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-alert v-if="trackingError" type="error" dense text class="mb-4">{{
            trackingError
          }}</v-alert>

          <!-- Hand delivery info for CRCHAH -->
          <v-alert
            v-if="trackingDialogPrefix && isHandDeliveryPrefix(trackingDialogPrefix)"
            type="info"
            dense
            text
            class="mb-4"
          >
            <v-icon left small>mdi-hand-extended-outline</v-icon>
            This vendor's items are hand-delivered. Use the notes field for delivery remarks.
          </v-alert>

          <div
            v-for="(tItem, idx) in trackingItems"
            :key="tItem.sku"
            class="mb-5"
          >
            <!-- SKU + name header -->
            <div class="d-flex align-center mb-3" style="gap: 8px">
              <v-chip
                x-small
                color="primary"
                outlined
                class="font-weight-bold"
                >{{ tItem.sku }}</v-chip
              >
              <span
                class="text-body-2 grey--text text--darken-2"
                style="
                  flex: 1;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                "
                >{{ tItem.name }}</span
              >
            </div>

            <!-- For hand-delivery items (CRCHAH): show notes field only -->
            <template v-if="tItem.isHandDelivery">
              <v-textarea
                v-model="trackingItems[idx].notes"
                label="Delivery Notes / Remarks"
                outlined
                dense
                hide-details
                rows="2"
                auto-grow
                prepend-inner-icon="mdi-note-text-outline"
              />
            </template>

            <!-- For courier-shipped items: show shipping ID + link -->
            <template v-else>
              <!-- Shipping ID row -->
              <div class="d-flex align-center mb-2" style="gap: 8px">
                <v-text-field
                  v-model="trackingItems[idx].shippingId"
                  label="Vendor Shipping ID"
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-identifier"
                  clearable
                  style="flex: 1"
                />
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      v-bind="attrs"
                      v-on="on"
                      :disabled="!tItem.shippingId"
                      @click="copyToClipboard(tItem.shippingId, 'Shipping ID')"
                    >
                      <v-icon>mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                  <span>Copy Shipping ID</span>
                </v-tooltip>
              </div>

              <!-- Tracking Link row -->
              <div class="d-flex align-center" style="gap: 8px">
                <v-text-field
                  v-model="trackingItems[idx].shippingLink"
                  label="Vendor Tracking Link"
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-link"
                  clearable
                  style="flex: 1"
                />
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      v-bind="attrs"
                      v-on="on"
                      :disabled="!tItem.shippingLink"
                      @click="
                        copyToClipboard(tItem.shippingLink, 'Tracking Link')
                      "
                    >
                      <v-icon>mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                  <span>Copy Tracking Link</span>
                </v-tooltip>
              </div>
            </template>

            <v-divider v-if="idx < trackingItems.length - 1" class="mt-4" />
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-4 py-3">
          <v-spacer />
          <v-btn text @click="trackingDialog = false" :disabled="trackingSaving"
            >Cancel</v-btn
          >
          <v-btn
            depressed
            color="primary"
            @click="saveVendorTracking"
            :loading="trackingSaving"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Customer Shipping Dialog -->
    <v-dialog
      v-model="customerShippingDialog"
      max-width="560"
      persistent
      content-class="customer-ship-dialog-overlay"
    >
      <v-card v-if="customerShippingOrder" rounded="lg" elevation="10" class="customer-ship-dialog">
        <div class="customer-ship-dialog__header pa-5 pb-4">
          <div class="d-flex align-start" style="gap: 14px">
            <v-avatar size="48" color="primary lighten-5" class="flex-shrink-0">
              <v-icon color="primary darken-1">mdi-truck-delivery-outline</v-icon>
            </v-avatar>
            <div class="flex-grow-1 min-width-0">
              <div class="text-h6 font-weight-bold grey--text text--darken-3 line-height-tight">
                Ship to Customer
              </div>
              <div class="text-body-2 grey--text text--darken-1 mt-1">
                Order <span class="font-weight-medium">#{{ customerShippingOrder.order_id }}</span>
                <span class="mx-1 grey--text">·</span>
                {{ customerShippingOrder.customer }}
              </div>
              <div v-if="customerShippingPrefixes.length" class="d-flex flex-wrap mt-2" style="gap: 4px">
                <v-chip
                  v-for="p in customerShippingPrefixes"
                  :key="p"
                  x-small
                  label
                  outlined
                  color="primary"
                  class="font-weight-medium"
                >
                  {{ p }}
                </v-chip>
              </div>
            </div>
            <v-btn
              icon
              small
              class="grey--text"
              :disabled="customerShippingSaving"
              @click="customerShippingDialog = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <v-divider />

        <v-card-text class="customer-ship-dialog__body px-5 py-5">
          <v-alert
            v-if="customerShippingError"
            type="error"
            dense
            outlined
            class="mb-4 text-body-2"
          >
            {{ customerShippingError }}
          </v-alert>

          <div
            v-if="customerShippingMode === 'ship' && customerShippingOrder && getCourierGroupsReadyToShip(customerShippingOrder).length >= 2"
            class="mb-4"
          >
            <v-switch
              v-model="customerShippingIncludeAllReady"
              dense
              hide-details
              color="primary"
              class="mt-0 customer-ship-combined-switch"
              @change="onCombinedShipToggle"
            >
              <template v-slot:label>
                <span class="text-body-2 grey--text text--darken-2">
                  Ship all ready import lines in one package
                  <span class="grey--text">({{ getReadyToShipLineItems(customerShippingOrder).length }} items)</span>
                </span>
              </template>
            </v-switch>
          </div>

          <div class="customer-ship-section mb-5">
            <div class="customer-ship-section__label mb-3">
              <v-icon small color="grey darken-1" class="mr-2">mdi-package-variant-closed</v-icon>
              Items in this shipment
            </div>
            <v-alert
              v-if="customerShippingPrefixes.length >= 2"
              dense
              outlined
              color="primary"
              class="mb-3 text-body-2"
              icon="mdi-package-variant"
            >
              One tracking entry applies to every selected item below — no need to ship each vendor separately.
            </v-alert>
            <div class="customer-ship-items">
              <v-list dense class="py-0">
                <v-list-item
                  v-for="item in customerShippingAvailableItems"
                  :key="item.sku"
                  class="customer-ship-item px-3"
                >
                  <v-list-item-action class="mr-3 my-2">
                    <v-checkbox
                      v-model="customerShippingForm.selectedItems"
                      :value="item.sku"
                      hide-details
                      color="primary"
                      class="mt-0 pt-0"
                    />
                  </v-list-item-action>
                  <v-list-item-content class="py-2">
                    <v-list-item-title class="d-flex align-center flex-wrap text-body-2 font-weight-bold primary--text text--darken-2">
                      <v-chip x-small label class="mr-2 mb-0 grey lighten-3" style="height: 18px">
                        {{ getSkuPrefix(item.sku) }}
                      </v-chip>
                      {{ item.sku }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption grey--text text--darken-2 text-wrap mt-1">
                      {{ item.name }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="!customerShippingAvailableItems.length" class="px-3">
                  <v-list-item-content>
                    <v-list-item-subtitle class="text-caption">No items available</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </div>

          <div class="customer-ship-section">
            <div class="customer-ship-section__label mb-3">
              <v-icon small color="grey darken-1" class="mr-2">mdi-map-marker-path</v-icon>
              Tracking details
              <span class="customer-ship-optional">Optional</span>
            </div>

            <v-alert
              dense
              outlined
              color="blue-grey"
              class="customer-ship-hint mb-4 text-body-2"
              icon="mdi-information-outline"
            >
              Leave tracking empty and choose <strong>Mark shipped</strong> to update import status only.
              Add courier details to enable the WhatsApp message copy.
            </v-alert>

            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="customerShippingForm.shippingCompany"
                  label="Courier / company"
                  placeholder="e.g. DTDC, Blue Dart"
                  outlined
                  dense
                  hide-details
                  class="mb-4"
                  prepend-inner-icon="mdi-office-building-outline"
                  background-color="grey lighten-5"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="customerShippingForm.trackingId"
                  label="Tracking ID"
                  placeholder="AWB or consignment no."
                  outlined
                  dense
                  hide-details
                  class="mb-4"
                  prepend-inner-icon="mdi-barcode"
                  background-color="grey lighten-5"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="customerShippingForm.trackingLink"
                  label="Tracking link"
                  placeholder="https://…"
                  outlined
                  dense
                  hide-details
                  class="mb-4"
                  prepend-inner-icon="mdi-link-variant"
                  background-color="grey lighten-5"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="customerShippingForm.shippingDate"
                  label="Ship date"
                  type="date"
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-calendar-outline"
                  background-color="grey lighten-5"
                />
              </v-col>
            </v-row>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="customer-ship-dialog__actions px-5 py-4">
          <v-btn
            text
            class="text-none grey--text text--darken-2"
            :disabled="customerShippingSaving"
            @click="customerShippingDialog = false"
          >
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
            outlined
            color="primary"
            class="text-none mr-2"
            :loading="customerShippingSaving"
            @click="saveCustomerShipping(true)"
          >
            Mark shipped
          </v-btn>
          <v-btn
            depressed
            color="primary"
            class="text-none px-5"
            :loading="customerShippingSaving"
            @click="saveCustomerShipping(false)"
          >
            <v-icon left small>mdi-check-circle-outline</v-icon>
            Save tracking &amp; ship
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Shipment Detail Dialog -->
    <v-dialog v-model="shipmentDetailDialog" max-width="500" persistent>
      <v-card v-if="shipmentDetailData" rounded="lg">
        <v-card-title class="d-flex align-center pb-1" style="gap: 8px">
          <v-icon color="green darken-2">mdi-package-variant-closed</v-icon>
          <span class="text-subtitle-1 font-weight-bold">{{ shipmentDetailEditing ? 'Edit Package' : 'Package Details' }}</span>
          <v-spacer />
          <v-btn icon small @click="closeShipmentDetail"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-alert v-if="shipmentDetailError" type="error" dense text class="mb-3">{{ shipmentDetailError }}</v-alert>

          <!-- VIEW MODE -->
          <template v-if="!shipmentDetailEditing">
            <v-simple-table dense class="mb-4">
              <tbody>
                <tr>
                  <td class="text-caption grey--text text--darken-1 font-weight-medium" style="width:140px">Shipping Company</td>
                  <td class="text-body-2 font-weight-bold">{{ shipmentDetailData.shipping_company }}</td>
                </tr>
                <tr>
                  <td class="text-caption grey--text text--darken-1 font-weight-medium">Tracking ID</td>
                  <td>
                    <span class="text-body-2 font-weight-bold">{{ shipmentDetailData.tracking_id }}</span>
                    <v-btn icon x-small class="ml-1" @click="copyToClipboard(shipmentDetailData.tracking_id, 'Tracking ID')">
                      <v-icon x-small color="grey">mdi-content-copy</v-icon>
                    </v-btn>
                  </td>
                </tr>
                <tr v-if="shipmentDetailData.tracking_link">
                  <td class="text-caption grey--text text--darken-1 font-weight-medium">Tracking Link</td>
                  <td>
                    <a :href="shipmentDetailData.tracking_link" target="_blank" class="text-body-2 text-decoration-none blue--text">{{ shipmentDetailData.tracking_link }}</a>
                  </td>
                </tr>
                <tr v-if="shipmentDetailData.shipping_date">
                  <td class="text-caption grey--text text--darken-1 font-weight-medium">Shipping Date</td>
                  <td class="text-body-2">{{ shipmentDetailData.shipping_date }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div class="text-subtitle-2 font-weight-bold mb-2">Items in this Package</div>
            <v-list dense class="pa-0 rounded" outlined style="max-height: 200px; overflow-y: auto;">
              <v-list-item v-for="sku in (shipmentDetailData.items || [])" :key="sku">
                <v-list-item-icon class="mr-3 my-auto">
                  <v-icon small color="green darken-1">mdi-check-circle</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium">{{ sku }}</v-list-item-title>
                  <v-list-item-subtitle v-if="shipmentDetailItemNames[sku]" class="text-caption text-wrap">{{ shipmentDetailItemNames[sku] }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="!shipmentDetailData.items || !shipmentDetailData.items.length">
                <v-list-item-content>
                  <v-list-item-subtitle class="text-caption grey--text">All items in this vendor group</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </template>

          <!-- EDIT MODE -->
          <template v-if="shipmentDetailEditing">
            <v-text-field v-model="shipmentEditForm.shipping_company" label="Shipping Company" outlined dense class="mb-1" />
            <v-text-field v-model="shipmentEditForm.tracking_id" label="Tracking ID" outlined dense class="mb-1" />
            <v-text-field v-model="shipmentEditForm.tracking_link" label="Tracking Link (optional)" outlined dense class="mb-1" />
            <v-text-field v-model="shipmentEditForm.shipping_date" label="Shipping Date" type="date" outlined dense />
          </template>

          <!-- DELETE CONFIRMATION -->
          <v-alert v-if="shipmentDeleteConfirm" type="warning" dense prominent class="mt-3 mb-0">
            <div class="text-body-2 font-weight-medium">Are you sure you want to delete this package?</div>
            <div class="d-flex mt-2" style="gap: 8px">
              <v-btn small depressed color="red darken-1" dark :loading="shipmentDetailSaving" @click="confirmDeleteShipment">Yes, Delete</v-btn>
              <v-btn small outlined @click="shipmentDeleteConfirm = false">Cancel</v-btn>
            </div>
          </v-alert>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-4 py-3">
          <!-- Delete button (left side) -->
          <v-btn
            v-if="!shipmentDetailEditing && !shipmentDeleteConfirm"
            text
            small
            color="red darken-1"
            class="text-none"
            @click="shipmentDeleteConfirm = true"
          >
            <v-icon left small>mdi-delete-outline</v-icon>
            Delete
          </v-btn>
          <v-spacer />
          <!-- View mode actions -->
          <template v-if="!shipmentDetailEditing">
            <v-btn
              v-if="!shipmentDeleteConfirm && shipmentDetailOrder && customerShipWhatsAppMessage(shipmentDetailOrder, shipmentDetailData)"
              depressed
              small
              color="#25D366"
              dark
              class="text-none mr-2"
              @click="copyCustomerShipWhatsApp(shipmentDetailOrder, shipmentDetailData)"
            >
              <v-icon left small>mdi-whatsapp</v-icon>
              Copy WhatsApp
            </v-btn>
            <v-btn text small class="text-none" @click="closeShipmentDetail">Close</v-btn>
            <v-btn
              v-if="!shipmentDeleteConfirm"
              depressed
              small
              color="primary"
              class="text-none"
              @click="startEditShipment"
            >
              <v-icon left small>mdi-pencil-outline</v-icon>
              Edit
            </v-btn>
          </template>
          <!-- Edit mode actions -->
          <template v-if="shipmentDetailEditing">
            <v-btn text small class="text-none" @click="shipmentDetailEditing = false" :disabled="shipmentDetailSaving">Cancel</v-btn>
            <v-btn
              depressed
              small
              color="primary"
              class="text-none"
              :loading="shipmentDetailSaving"
              @click="saveEditShipment"
            >
              <v-icon left small>mdi-content-save-outline</v-icon>
              Save Changes
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";
import moment from "moment-timezone";
import * as XLSX from "xlsx";

const DEFAULT_PREFIXES = [
  "CRCJPAN",
  "CRCJAPMUT",
  "CRCJAPMNO",
  "CRCJAPMX2",
  "CRCBTS",
  "CRCHAH",
];

// Prefixes where items are hand-delivered (no courier tracking)
const HAND_DELIVERY_PREFIXES = ["CRCHAH"];

const STATUS_COLORS = {
  processing: { bg: "blue lighten-5", text: "blue darken-3" },
  packed: { bg: "purple lighten-5", text: "purple darken-3" },
  ready: { bg: "cyan lighten-5", text: "cyan darken-3" },
  shipped: { bg: "orange lighten-5", text: "orange darken-3" },
  completed: { bg: "green lighten-5", text: "green darken-3" },
  resolved: { bg: "teal lighten-5", text: "teal darken-3" },
  cancelled: { bg: "red lighten-5", text: "red darken-3" },
};

export default {
  name: "ExternalOrderTracker",
  data() {
    return {
      ENDPOINT: process.env.VUE_APP_ENDPOINT,
      loading: false,
      syncing: false,
      copiedSku: null,
      syncId: null,
      eventSource: null,
      syncProgress: {
        percent: 0,
        page: 0,
        totalPages: 0,
        synced: 0,
        totalOrders: 0,
      },
      errorMessage: "",
      syncMessage: "",
      orders: [],
      searchInput: "",
      searchQuery: "",
      statusFilter: null,
      skuFilter: [],
      page: 1,
      totalPages: 1,
      totalOrders: 0,
      itemsPerPage: 20,
      headers: [
        { text: "Order", value: "order_id", width: 90 },
        { text: "Date", value: "order_date", width: 110 },
        { text: "Customer", value: "customer" },
        { text: "Woo Status", value: "status", width: 120 },
        { text: "Total", value: "total", width: 100 },
        { text: "Matched Items", value: "matched_items" },
        { text: "Matched SKUs", value: "matched_skus", width: 200 },
        {
          text: "Import Ship Status",
          value: "supplier_status",
          width: 340,
          sortable: false,
        },
      ],
      skuPrefixes: DEFAULT_PREFIXES,
      statusOptions: [
        "processing",
        "packed",
        "ready",
        "shipped",
        "completed",
        "resolved",
      ],
      syncStatus: { totalSynced: 0, externalCount: 0, lastSyncedAt: null },
      trackingDialog: false,
      trackingDialogPrefix: null,
      trackingOrder: null,
      trackingItems: [],
      trackingSaving: false,
      trackingError: "",
      exporting: false,
      customerShippingDialog: false,
      customerShippingOrder: null,
      customerShippingPrefix: null,
      customerShippingPrefixes: [],
      customerShippingMode: "ship",
      customerShippingIncludeAllReady: true,
      customerShippingOpenedFromPrefix: null,
      customerShippingError: "",
      customerShippingSaving: false,
      customerShippingAvailableItems: [],
      customerShippingForm: {
        shippingCompany: "",
        trackingId: "",
        trackingLink: "",
        shippingDate: moment().format("YYYY-MM-DD"),
        selectedItems: []
      },
      shipmentDetailDialog: false,
      shipmentDetailData: null,
      shipmentDetailItemNames: {},
      shipmentDetailEditing: false,
      shipmentDetailSaving: false,
      shipmentDetailError: "",
      shipmentDeleteConfirm: false,
      shipmentEditForm: {},
      shipmentDetailOrder: null,
      shipmentDetailIndex: -1,
    };
  },
  mounted() {
    this.skuPrefixes = this.getSkuPrefixes();
    this.checkSyncStatus();
    this.fetchOrders();
  },
  methods: {
    getLineMeta(line, key) {
      if (!line.meta_data) return null;
      const meta = line.meta_data.find((m) => m.key === key);
      return meta ? meta.value : null;
    },
    copySku(sku) {
      const numericOnly = sku.replace(/[^0-9]/g, "");
      navigator.clipboard.writeText(numericOnly);
      this.copiedSku = sku;
      setTimeout(() => { this.copiedSku = null; }, 1500);
    },
    harmonieUrl(sku) {
      const productId = sku.replace(/^CRCHAH/i, "").replace(/[^0-9]/g, "");
      return productId
        ? `https://www.harmonieaudio.com/?post_type=product&p=${productId}`
        : "#";
    },
    getSkuPrefixes() {
      const configured = (process.env.VUE_APP_EXTERNAL_SKU_PREFIXES || "")
        .split(",")
        .map((p) => p.trim().toUpperCase())
        .filter(Boolean);
      return configured.length ? configured : DEFAULT_PREFIXES;
    },
    formatDate(date) {
      return moment(date).format("DD MMM YYYY, hh:mm A");
    },
    statusColor(status) {
      return (
        STATUS_COLORS[status] || { bg: "grey lighten-3", text: "grey darken-2" }
      );
    },
    supplierStatusLabel(s) {
      return (
        {
          processing: "Processing",
          vendor_placed: "Placed to Vendor",
          vendor_received: "Received from Vendor",
          shipped_to_customer: "Shipped to Customer",
        }[s] || s
      );
    },
    supplierStatusColor(s) {
      return (
        {
          processing: { bg: "blue lighten-5", text: "blue darken-3" },
          vendor_placed: { bg: "indigo lighten-5", text: "indigo darken-3" },
          vendor_received: { bg: "purple lighten-5", text: "purple darken-3" },
          shipped_to_customer: {
            bg: "green lighten-5",
            text: "green darken-3",
          },
        }[s] || { bg: "grey lighten-3", text: "grey darken-2" }
      );
    },
    // Card-based vendor group helpers
    vendorBorderColor(status) {
      return ({
        processing: '#1976D2',
        vendor_placed: '#5C6BC0',
        vendor_received: '#7B1FA2',
        shipped_to_customer: '#2E7D32',
      })[status] || '#9E9E9E';
    },
    vendorBgColor(status) {
      return ({
        processing: '#E3F2FD',
        vendor_placed: '#E8EAF6',
        vendor_received: '#F3E5F5',
        shipped_to_customer: '#E8F5E9',
      })[status] || '#F5F5F5';
    },
    vendorStatusIconColor(status) {
      return ({
        processing: '#1565C0',
        vendor_placed: '#3949AB',
        vendor_received: '#6A1B9A',
        shipped_to_customer: '#1B5E20',
      })[status] || '#616161';
    },
    vendorStatusIcon(status) {
      return ({
        processing: 'mdi-progress-clock',
        vendor_placed: 'mdi-cart-arrow-right',
        vendor_received: 'mdi-package-down',
        shipped_to_customer: 'mdi-truck-check',
      })[status] || 'mdi-help-circle-outline';
    },
    vendorPrevStatus(status) {
      return ({
        vendor_placed: 'processing',
        vendor_received: 'vendor_placed',
        shipped_to_customer: 'vendor_received',
      })[status] || 'processing';
    },
    hasTrackingData(item) {
      return (item.vendorTracking || []).some(
        (t) => t.shippingId || t.shippingLink || t.notes,
      );
    },
    // Get the vendor prefix for a given SKU
    getSkuPrefix(sku) {
      const upper = (sku || "").trim().toUpperCase();
      // Sort longest-first so CRCJAPMUT matches before CRCJPAN
      const sorted = [...this.skuPrefixes].sort((a, b) => b.length - a.length);
      return sorted.find((prefix) => upper.startsWith(prefix)) || "OTHER";
    },
    // Check if a prefix is hand-delivery (no courier tracking)
    isHandDeliveryPrefix(prefix) {
      return HAND_DELIVERY_PREFIXES.includes((prefix || "").toUpperCase());
    },
    // Group matched items by vendor prefix for per-vendor status display
    getVendorGroups(item) {
      const groups = {};
      (item.matched_items || []).forEach((mi) => {
        const prefix = this.getSkuPrefix(mi.sku);
        if (!groups[prefix]) {
          groups[prefix] = {
            prefix,
            items: [],
            isHandDelivery: this.isHandDeliveryPrefix(prefix),
          };
        }
        groups[prefix].items.push(mi);
      });
      const vendorStatuses = item.vendorStatuses || {};
      const vendorKeys = Object.keys(vendorStatuses);
      Object.values(groups).forEach((g) => {
        if (vendorStatuses[g.prefix]) {
          g.status = vendorStatuses[g.prefix];
        } else if (vendorKeys.length === 0 && item.supplierStatus) {
          g.status = item.supplierStatus;
        } else {
          g.status = "processing";
        }
      });
      return Object.values(groups);
    },
    formatWhatsAppItemLabel(name) {
      if (!name) return "";
      return String(name)
        .replace(/\s+/g, " ")
        .trim();
    },
    customerShipWhatsAppMessage(order, shipment) {
      if (!shipment || !shipment.tracking_id || !shipment.shipping_company) {
        return null;
      }
      const firstName = (order.customer || "there").trim().split(/\s+/)[0];
      const itemLabels = (shipment.items || [])
        .map((sku) => {
          const line = (order.matched_items || []).find((m) => m.sku === sku);
          return line
            ? this.formatWhatsAppItemLabel(line.name)
            : sku;
        })
        .filter(Boolean);

      const itemCount = itemLabels.length;
      const lines = [
        `Hi ${firstName}! 👋`,
        "",
        "Great news — your import order from *Calcutta Records* has shipped and is on its way to you! 🎵📦",
        "",
        `*Order #${order.order_id}*`,
      ];

      if (shipment.shipping_date) {
        lines.push(`Ship date: ${shipment.shipping_date}`);
      }

      lines.push(
        "",
        "🚚 *Tracking details*",
        `Courier: ${shipment.shipping_company}`,
        `Tracking ID: ${shipment.tracking_id}`,
      );

      if (shipment.tracking_link) {
        lines.push(`Track your package: ${shipment.tracking_link}`);
      }

      if (itemLabels.length) {
        lines.push(
          "",
          itemCount === 1
            ? "🎶 *In this shipment*"
            : `🎶 *In this shipment (${itemCount} items)*`,
        );
        itemLabels.forEach((label, i) => {
          lines.push(`${i + 1}. ${label}`);
        });
      }

      lines.push(
        "",
        "If anything looks off or you need help, just reply to this message — we're happy to assist.",
        "",
        "Thank you for choosing Calcutta Records. Enjoy the spin! 🙏",
        "https://calcuttarecords.com",
      );

      return lines.join("\n");
    },
    copyCustomerShipWhatsApp(order, shipment) {
      const message = this.customerShipWhatsAppMessage(order, shipment);
      if (!message) return;
      this.copyToClipboard(message, "WhatsApp message");
    },
    // Check if a specific vendor prefix has tracking data
    hasTrackingDataForPrefix(item, prefix) {
      return (item.vendorTracking || []).some((t) => {
        const tPrefix = this.getSkuPrefix(t.sku);
        return tPrefix === prefix && (t.shippingId || t.shippingLink || t.notes);
      });
    },
    copyToClipboard(text, label) {
      navigator.clipboard.writeText(text).then(() => {
        this.syncMessage = `${label} copied to clipboard`;
        setTimeout(() => {
          if (this.syncMessage.includes("copied")) this.syncMessage = "";
        }, 2500);
      });
    },
    async exportToExcel() {
      this.exporting = true;
      try {
        const params = {};
        if (this.searchQuery) params.search = this.searchQuery;
        if (this.statusFilter) params.status = this.statusFilter;
        if (this.skuFilter.length)
          params.skuPrefixes = this.skuFilter.join(",");

        const { data } = await axios.get(
          `${this.ENDPOINT}/external-orders/export`,
          { params },
        );
        const orders = data.orders || [];

        const LINK_PREFIXES = ["CRCJPAN", "CRCJAPMUT"];
        const rows = [];

        for (const order of orders) {
          const matchedItems = order.matched_items || [];
          const trackingMap = {};
          (order.vendorTracking || []).forEach((t) => {
            trackingMap[(t.sku || "").toUpperCase()] = t;
          });
          const vendorStatuses = order.vendorStatuses || {};

          matchedItems.forEach((item, idx) => {
            const sku = (item.sku || "").toUpperCase();
            const tracking = trackingMap[sku] || {};
            const itemPrefix = this.getSkuPrefix(item.sku);
            const itemVendorStatus =
              vendorStatuses[itemPrefix] ||
              (Object.keys(vendorStatuses).length === 0
                ? order.supplierStatus
                : "processing") ||
              "processing";
            const isHD = this.isHandDeliveryPrefix(itemPrefix);
            const dispatched = [
              "vendor_received",
              "shipped_to_customer",
            ].includes(itemVendorStatus)
              ? "Yes"
              : "No";
            const itemMeta = item.meta_data || [];
            const getMeta = (keys) =>
              itemMeta.find((m) => keys.includes(m.key))?.value || "";

            const crcPrice = item.price || "";
            const vendorPrice = getMeta([
              "vendor_price",
              "_vendor_price",
              "cost_price",
              "_cost_price",
            ]);
            const link = LINK_PREFIXES.some((p) => sku.startsWith(p))
              ? getMeta([
                  "url",
                  "_url",
                  "product_url",
                  "_product_url",
                  "discogs_url",
                  "_discogs_url",
                  "vendor_url",
                ])
              : "";

            rows.push({
              Order: idx === 0 ? `#${order.id}` : "",
              Date:
                idx === 0
                  ? moment(order.date_created).format("DD MMM YYYY")
                  : "",
              "Item Name": item.name || "",
              SKU: item.sku || "",
              Vendor: itemPrefix,
              "CRC Price": crcPrice,
              "Vendor Price": vendorPrice,
              Link: link,
              "Vendor Status": this.supplierStatusLabel(itemVendorStatus),
              "Dispatched by Vendor": dispatched,
              "Delivery Type": isHD ? "Hand Delivered" : "Courier",
              "Tracking ID": tracking.shippingId || "",
              "Tracking Link": tracking.shippingLink || "",
              "Notes": tracking.notes || "",
            });
          });
        }

        if (!rows.length) {
          this.errorMessage = "No data to export.";
          return;
        }

        const ws = XLSX.utils.json_to_sheet(rows);
        ws["!cols"] = Object.keys(rows[0]).map((k) => ({
          wch: Math.max(k.length + 4, 22),
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "External Orders");
        XLSX.writeFile(
          wb,
          `external-orders-${moment().format("YYYY-MM-DD")}.xlsx`,
        );
      } catch (e) {
        console.error("Export error:", e);
        this.errorMessage = "Export failed. Please try again.";
      } finally {
        this.exporting = false;
      }
    },
    openTrackingDialog(order, prefix) {
      this.trackingOrder = order;
      this.trackingDialogPrefix = prefix || null;
      const existing = {};
      (order.vendorTracking || []).forEach((t) => {
        existing[t.sku] = t;
      });
      // Filter matched items to only those belonging to the specified prefix
      const filteredItems = prefix
        ? order.matched_items.filter((item) => this.getSkuPrefix(item.sku) === prefix)
        : order.matched_items;
      this.trackingItems = filteredItems.map((item) => {
        const ex = existing[item.sku] || {};
        const isHD = this.isHandDeliveryPrefix(this.getSkuPrefix(item.sku));
        return {
          sku: item.sku || "",
          name: item.name || "",
          isHandDelivery: isHD,
          shippingId: ex.shippingId || "",
          shippingLink: ex.shippingLink || "",
          notes: ex.notes || "",
        };
      });
      this.trackingError = "";
      this.trackingDialog = true;
    },
    async saveVendorTracking() {
      this.trackingSaving = true;
      this.trackingError = "";
      try {
        // Build tracking for items in this dialog
        const dialogTracking = this.trackingItems.map((t) => ({
          sku: t.sku,
          shippingId: t.isHandDelivery ? "" : t.shippingId,
          shippingLink: t.isHandDelivery ? "" : t.shippingLink,
          notes: t.notes || "",
        }));
        // Merge with existing tracking data for items NOT in this dialog
        const dialogSkus = new Set(dialogTracking.map((t) => t.sku));
        const existingOther = (this.trackingOrder.vendorTracking || []).filter(
          (t) => !dialogSkus.has(t.sku),
        );
        const vendorTracking = [...existingOther, ...dialogTracking];
        await axios.patch(
          `${this.ENDPOINT}/external-orders/${this.trackingOrder.order_id}/vendor-tracking`,
          { vendorTracking },
        );
        this.$set(this.trackingOrder, "vendorTracking", vendorTracking);
        this.trackingDialog = false;
      } catch (e) {
        console.error("Error saving vendor tracking:", e);
        this.trackingError = "Could not save tracking info. Please try again.";
      } finally {
        this.trackingSaving = false;
      }
    },
    // Per-vendor status update (new endpoint)
    async updateVendorStatus(item, prefix, newStatus) {
      this.$set(item, "_statusLoading", prefix);
      try {
        const { data } = await axios.patch(
          `${this.ENDPOINT}/external-orders/${item.order_id}/vendor-status`,
          { prefix, supplierStatus: newStatus },
        );
        // Update local vendorStatuses
        const vs = { ...(item.vendorStatuses || {}) };
        vs[prefix] = newStatus;
        this.$set(item, "vendorStatuses", vs);
        // Update overall supplierStatus from server response
        if (data.supplierStatus) {
          this.$set(item, "supplierStatus", data.supplierStatus);
        }
      } catch (e) {
        console.error("Error updating vendor status:", e);
        this.errorMessage = "Could not update vendor status. Please try again.";
      } finally {
        this.$set(item, "_statusLoading", false);
      }
    },
    // Legacy per-order status update (kept for backward compat)
    async updateSupplierStatus(item, newStatus) {
      this.$set(item, "_statusLoading", true);
      try {
        await axios.patch(
          `${this.ENDPOINT}/external-orders/${item.order_id}/supplier-status`,
          { supplierStatus: newStatus },
        );
        this.$set(item, "supplierStatus", newStatus);
      } catch (e) {
        console.error("Error updating supplier status:", e);
        this.errorMessage =
          "Could not update supplier status. Please try again.";
      } finally {
        this.$set(item, "_statusLoading", false);
      }
    },

    // Search handlers
    onSearchEnter() {
      this.applyFilters();
    },
    onSearchClear() {
      this.searchInput = "";
      this.searchQuery = "";
      this.page = 1;
      this.fetchOrders();
    },
    applyFilters() {
      this.searchQuery = this.searchInput;
      this.page = 1;
      this.fetchOrders();
    },
    onStatusChange() {
      this.page = 1;
      this.fetchOrders();
    },
    onSkuFilterChange() {
      this.page = 1;
      this.fetchOrders();
    },
    onPageChange() {
      // Called by v-pagination @input — page is already updated by v-model
      this.fetchOrders();
    },
    refreshOrders() {
      this.page = 1;
      this.fetchOrders();
    },

    // Customer Shipping logic
    shipmentCoversPrefix(shipment, prefix) {
      if (!shipment || !prefix) return false;
      const p = String(prefix).toUpperCase();
      if (String(shipment.prefix || "").toUpperCase() === p) return true;
      return (shipment.prefixes || []).some(
        (x) => String(x || "").toUpperCase() === p,
      );
    },
    getCustomerShipmentsForPrefix(order, prefix) {
      if (!order.customerShipments) return [];
      return order.customerShipments.filter((s) =>
        this.shipmentCoversPrefix(s, prefix),
      );
    },
    getCourierGroupsReadyToShip(order) {
      return this.getVendorGroups(order).filter(
        (g) => !g.isHandDelivery && g.status === "vendor_received",
      );
    },
    getReadyToShipLineItems(order) {
      const readyPrefixes = this.getCourierGroupsReadyToShip(order).map(
        (g) => g.prefix,
      );
      return (order.matched_items || []).filter((mi) => {
        const p = this.getSkuPrefix(mi.sku);
        return readyPrefixes.includes(p) && !this.isHandDeliveryPrefix(p);
      });
    },
    openShipmentDetail(shipment, order) {
      // Find the index of this shipment in the order's customerShipments array
      const idx = (order.customerShipments || []).indexOf(shipment);
      this.shipmentDetailData = shipment;
      this.shipmentDetailOrder = order;
      this.shipmentDetailIndex = idx;
      this.shipmentDetailEditing = false;
      this.shipmentDetailSaving = false;
      this.shipmentDetailError = "";
      this.shipmentDeleteConfirm = false;
      const nameMap = {};
      (order.matched_items || []).forEach(item => {
        if (item.sku) nameMap[item.sku] = item.name || '';
      });
      this.shipmentDetailItemNames = nameMap;
      this.shipmentDetailDialog = true;
    },
    closeShipmentDetail() {
      this.shipmentDetailDialog = false;
      this.shipmentDetailEditing = false;
      this.shipmentDeleteConfirm = false;
      this.shipmentDetailError = "";
    },
    startEditShipment() {
      this.shipmentEditForm = {
        shipping_company: this.shipmentDetailData.shipping_company || "",
        tracking_id: this.shipmentDetailData.tracking_id || "",
        tracking_link: this.shipmentDetailData.tracking_link || "",
        shipping_date: this.shipmentDetailData.shipping_date || "",
      };
      this.shipmentDetailEditing = true;
      this.shipmentDeleteConfirm = false;
      this.shipmentDetailError = "";
    },
    async saveEditShipment() {
      if (!this.shipmentEditForm.tracking_id || !this.shipmentEditForm.shipping_company) {
        this.shipmentDetailError = "Tracking ID and Shipping Company are required.";
        return;
      }
      this.shipmentDetailSaving = true;
      this.shipmentDetailError = "";
      try {
        const wooId = this.shipmentDetailOrder.order_id;
        await axios.put(
          `${this.ENDPOINT}/external-orders/${wooId}/customer-shipping/${this.shipmentDetailIndex}`,
          this.shipmentEditForm
        );
        // Update local state
        Object.assign(this.shipmentDetailData, this.shipmentEditForm);
        this.shipmentDetailEditing = false;
        this.syncMessage = "Package updated successfully.";
        setTimeout(() => { if (this.syncMessage.includes("updated")) this.syncMessage = ""; }, 2500);
      } catch (e) {
        console.error("Error updating shipment:", e);
        this.shipmentDetailError = "Could not update package. Please try again.";
      } finally {
        this.shipmentDetailSaving = false;
      }
    },
    async confirmDeleteShipment() {
      this.shipmentDetailSaving = true;
      this.shipmentDetailError = "";
      try {
        const wooId = this.shipmentDetailOrder.order_id;
        const response = await axios.delete(
          `${this.ENDPOINT}/external-orders/${wooId}/customer-shipping/${this.shipmentDetailIndex}`
        );
        // Remove from local state
        const shipments = this.shipmentDetailOrder.customerShipments || [];
        shipments.splice(this.shipmentDetailIndex, 1);
        this.$set(this.shipmentDetailOrder, 'customerShipments', [...shipments]);
        
        // Handle reverted status
        if (response.data && response.data.revertedStatus && response.data.vendorStatuses) {
           this.$set(this.shipmentDetailOrder, 'vendorStatuses', response.data.vendorStatuses);
        }

        this.shipmentDetailDialog = false;
        this.syncMessage = "Package deleted.";
        setTimeout(() => { if (this.syncMessage.includes("deleted")) this.syncMessage = ""; }, 2500);
      } catch (e) {
        console.error("Error deleting shipment:", e);
        this.shipmentDetailError = "Could not delete package. Please try again.";
      } finally {
        this.shipmentDetailSaving = false;
      }
    },
    copyToClipboard(text, label) {
      navigator.clipboard.writeText(text).then(() => {
        this.syncMessage = `${label} copied to clipboard`;
        setTimeout(() => {
          if (this.syncMessage.includes("copied")) this.syncMessage = "";
        }, 2500);
      });
    },
    applyCustomerShippingItemList(order) {
      const prefix = this.customerShippingOpenedFromPrefix;
      let lineItems;
      if (this.customerShippingMode === "add" && prefix) {
        lineItems = (order.matched_items || []).filter(
          (item) => this.getSkuPrefix(item.sku) === prefix,
        );
      } else if (
        this.customerShippingIncludeAllReady &&
        this.getCourierGroupsReadyToShip(order).length >= 2
      ) {
        lineItems = this.getReadyToShipLineItems(order);
      } else if (prefix) {
        lineItems = (order.matched_items || []).filter(
          (item) => this.getSkuPrefix(item.sku) === prefix,
        );
      } else {
        lineItems = this.getReadyToShipLineItems(order);
        if (!lineItems.length && prefix) {
          lineItems = (order.matched_items || []).filter(
            (item) => this.getSkuPrefix(item.sku) === prefix,
          );
        }
      }

      this.customerShippingAvailableItems = lineItems;
      this.customerShippingPrefixes = [
        ...new Set(lineItems.map((i) => this.getSkuPrefix(i.sku))),
      ];
      const selected = new Set(this.customerShippingForm.selectedItems || []);
      this.customerShippingForm.selectedItems = lineItems
        .map((i) => i.sku)
        .filter((sku) => selected.has(sku));
      if (!this.customerShippingForm.selectedItems.length) {
        this.customerShippingForm.selectedItems = lineItems.map((i) => i.sku);
      }
    },
    onCombinedShipToggle() {
      if (!this.customerShippingOrder) return;
      this.applyCustomerShippingItemList(this.customerShippingOrder);
    },
    openCustomerShippingDialog(order, prefix, options = {}) {
      const mode = options.mode || "ship";
      this.customerShippingOrder = order;
      this.customerShippingPrefix = prefix;
      this.customerShippingMode = mode;
      this.customerShippingOpenedFromPrefix = prefix;
      this.customerShippingError = "";

      const multipleReady = this.getCourierGroupsReadyToShip(order).length >= 2;
      this.customerShippingIncludeAllReady = mode === "ship" && multipleReady;

      this.customerShippingForm = {
        shippingCompany: "",
        trackingId: "",
        trackingLink: "",
        shippingDate: moment().format("YYYY-MM-DD"),
        selectedItems: [],
      };
      this.applyCustomerShippingItemList(order);
      this.customerShippingDialog = true;
    },
    async saveCustomerShipping(markShippedOnly = false) {
      const trackingId = (this.customerShippingForm.trackingId || "").trim();
      const shippingCompany = (this.customerShippingForm.shippingCompany || "").trim();
      const hasTracking = Boolean(trackingId || shippingCompany);

      if (!markShippedOnly && hasTracking && (!trackingId || !shippingCompany)) {
        this.customerShippingError =
          "Enter both tracking ID and shipping company, or use Mark Shipped without tracking.";
        return;
      }
      if (!markShippedOnly && hasTracking && !this.customerShippingForm.selectedItems.length) {
        this.customerShippingError = "Select at least one item for this shipment.";
        return;
      }

      this.customerShippingSaving = true;
      this.customerShippingError = "";
      try {
        const { data } = await axios.post(
          `${this.ENDPOINT}/external-orders/${this.customerShippingOrder.order_id}/customer-shipping`,
          {
            trackingId: hasTracking && !markShippedOnly ? trackingId : "",
            shippingCompany: hasTracking && !markShippedOnly ? shippingCompany : "",
            trackingLink: this.customerShippingForm.trackingLink,
            shippingDate: this.customerShippingForm.shippingDate,
            items: this.customerShippingForm.selectedItems,
            prefix: this.customerShippingPrefix,
            prefixes: [
              ...new Set(
                this.customerShippingForm.selectedItems.map((sku) =>
                  this.getSkuPrefix(sku),
                ),
              ),
            ],
            markShippedOnly: markShippedOnly || !hasTracking,
          },
        );

        this.$set(
          this.customerShippingOrder,
          "customerShipments",
          data.customerShipments || [],
        );
        this.$set(
          this.customerShippingOrder,
          "vendorStatuses",
          data.vendorStatuses || this.customerShippingOrder.vendorStatuses || {},
        );
        if (data.supplierStatus) {
          this.$set(this.customerShippingOrder, "supplierStatus", data.supplierStatus);
        }

        this.customerShippingDialog = false;
      } catch (e) {
        console.error("Error saving customer shipping:", e);
        this.customerShippingError = "Could not save shipping info. Please check your connection.";
      } finally {
        this.customerShippingSaving = false;
      }
    },
    async markCustomerShippedWithoutTracking(item, prefix) {
      const readyItems = this.getReadyToShipLineItems(item);
      const readySkus = readyItems.map((m) => m.sku);
      const markAllReady =
        readyItems.length > 1 &&
        readyItems.some((m) => this.getSkuPrefix(m.sku) === prefix);

      const result = await this.$swal({
        title: markAllReady ? "Mark all ready lines as shipped?" : "Mark as shipped?",
        text: markAllReady
          ? `${readyItems.length} import lines will be marked shipped with no tracking. Add tracking later to enable WhatsApp copy.`
          : "Import status will be updated without tracking. No WhatsApp message will be generated until you add tracking.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: markAllReady ? "Mark all shipped" : "Mark Shipped",
      });
      if (!result.isConfirmed) return;

      const skusToMark = markAllReady
        ? readySkus
        : (item.matched_items || [])
            .filter((m) => this.getSkuPrefix(m.sku) === prefix)
            .map((m) => m.sku);
      const prefixesToMark = [...new Set(skusToMark.map((sku) => this.getSkuPrefix(sku)))];

      this.$set(item, "_statusLoading", prefix);
      try {
        const { data } = await axios.post(
          `${this.ENDPOINT}/external-orders/${item.order_id}/customer-shipping`,
          {
            prefix,
            prefixes: prefixesToMark,
            markShippedOnly: true,
            items: skusToMark,
          },
        );
        this.$set(item, "customerShipments", data.customerShipments || []);
        this.$set(item, "vendorStatuses", data.vendorStatuses || {});
        if (data.supplierStatus) {
          this.$set(item, "supplierStatus", data.supplierStatus);
        }
      } catch (e) {
        console.error("Error marking shipped:", e);
        this.errorMessage = "Could not update ship status. Please try again.";
      } finally {
        this.$set(item, "_statusLoading", false);
      }
    },
    async resetVendorPipeline(item, prefix) {
      const result = await this.$swal({
        title: "Reset import ship status?",
        text: "This clears customer tracking packages for this vendor and sets status back to Processing.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Reset",
        confirmButtonColor: "#d32f2f",
      });
      if (!result.isConfirmed) return;

      this.$set(item, "_statusLoading", prefix);
      try {
        const { data } = await axios.patch(
          `${this.ENDPOINT}/external-orders/${item.order_id}/vendor-reset`,
          { prefix },
        );
        this.$set(item, "vendorStatuses", data.vendorStatuses || {});
        this.$set(item, "customerShipments", data.customerShipments || []);
        if (data.supplierStatus) {
          this.$set(item, "supplierStatus", data.supplierStatus);
        }
        this.syncMessage = `Import ship status reset for ${prefix}`;
        setTimeout(() => {
          if (this.syncMessage.includes("reset")) this.syncMessage = "";
        }, 2500);
      } catch (e) {
        console.error("Error resetting vendor pipeline:", e);
        this.errorMessage = "Could not reset status. Please try again.";
      } finally {
        this.$set(item, "_statusLoading", false);
      }
    },

    // Sync status
    async checkSyncStatus() {
      try {
        const { data } = await axios.get(`${this.ENDPOINT}/orders/sync/status`);
        this.syncStatus = data;
      } catch (e) {
        console.error("Error checking sync status:", e);
      }
    },

    // Bulk sync via SSE
    runBulkSync() {
      this.syncing = true;
      this.syncMessage = "";
      this.errorMessage = "";
      this.syncProgress = {
        percent: 0,
        page: 0,
        totalPages: 0,
        synced: 0,
        totalOrders: 0,
      };

      // If no progress event arrives within 30 s, the connection is likely
      // being buffered or dropped by a proxy. Surface a clear error.
      const stuckTimer = setTimeout(() => {
        if (this.syncing && this.syncProgress.percent === 0) {
          this.errorMessage = "Sync is taking too long to start. The server may be unreachable or a proxy is blocking the stream. Try again.";
          this.closeSyncStream();
        }
      }, 30000);

      const es = new EventSource(`${this.ENDPOINT}/orders/sync/stream`);
      this.eventSource = es;

      es.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === "started") {
          this.syncId = data.syncId;
        } else if (data.type === "progress") {
          clearTimeout(stuckTimer);
          this.syncProgress = {
            percent: data.percent,
            page: data.page,
            totalPages: data.totalPages,
            synced: data.synced,
            totalOrders: data.totalOrders,
          };
        } else if (data.type === "complete" || data.type === "cancelled") {
          clearTimeout(stuckTimer);
          this.syncMessage = data.message;
          this.closeSyncStream();
          this.checkSyncStatus();
          this.page = 1;
          this.fetchOrders();
        } else if (data.type === "error") {
          clearTimeout(stuckTimer);
          this.errorMessage = `Sync error: ${data.message}`;
          this.closeSyncStream();
        }
      };

      es.onerror = () => {
        clearTimeout(stuckTimer);
        // SSE closes naturally after server ends the stream — only treat as error if still syncing
        if (this.syncing) {
          this.errorMessage = "Sync connection lost. Please try again.";
          this.closeSyncStream();
        }
      };
    },
    async cancelSync() {
      if (this.syncId) {
        try {
          await axios.post(`${this.ENDPOINT}/orders/sync/cancel`, {
            syncId: this.syncId,
          });
        } catch (e) {
          console.error("Cancel request failed:", e);
        }
      }
      this.closeSyncStream();
    },
    closeSyncStream() {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
      this.syncing = false;
      this.syncId = null;
    },

    // Fetch orders from MongoDB
    async fetchOrders() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const params = { page: this.page, per_page: this.itemsPerPage };
        if (this.searchQuery) params.search = this.searchQuery;
        if (this.statusFilter) params.status = this.statusFilter;
        if (this.skuFilter.length)
          params.skuPrefixes = this.skuFilter.join(",");

        const { data } = await axios.get(`${this.ENDPOINT}/external-orders`, {
          params,
        });

        this.orders = (data.orders || []).map((order) => ({
          ...order,
          order_id: order.id,
          order_date: moment(order.date_created).format("DD MMM YYYY"),
          customer: `${order.billing?.first_name || ""} ${
            order.billing?.last_name || ""
          }`.trim(),
          phone: order.billing?.phone || "",
          supplierStatus: order.supplierStatus || "processing",
          vendorStatuses: order.vendorStatuses || {},
          matched_items: order.matched_items || [],
          matched_skus: order.matched_skus || [],
          vendorTracking: order.vendorTracking || [],
        }));
        this.totalOrders = data.totalOrders || 0;
        this.totalPages = data.totalPages || 1;
      } catch (e) {
        console.error("Error fetching external orders:", e);
        this.errorMessage = "Could not load orders. Please try again.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.orders-table >>> .v-data-table__wrapper table {
  border-collapse: collapse;
}
.orders-table >>> tbody tr:hover {
  background-color: #f5f8ff !important;
}
.vendor-group-card {
  padding: 8px 10px;
  border-radius: 8px;
  transition: box-shadow 0.2s ease;
}
.vendor-group-card:hover {
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
}
.vendor-prefix-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #555;
  background: rgba(0,0,0,0.06);
  padding: 2px 6px;
  border-radius: 4px;
}
.vendor-status-label {
  font-size: 11px;
  font-weight: 600;
}
.shipment-row {
  transition: background 0.15s ease;
}
.shipment-row:hover {
  background: rgba(0,0,0,0.04) !important;
}

.customer-ship-dialog__header {
  background: linear-gradient(180deg, #fafbfc 0%, #ffffff 100%);
}

.customer-ship-dialog__body {
  background-color: #fff;
}

.customer-ship-section__label {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #424242;
}

.customer-ship-optional {
  margin-left: 8px;
  font-size: 0.6875rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9e9e9e;
}

.customer-ship-items {
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #fafafa;
  max-height: 220px;
  overflow-y: auto;
}

.customer-ship-item + .customer-ship-item {
  border-top: 1px solid #eeeeee;
}

.customer-ship-hint >>> .v-alert__wrapper {
  align-items: flex-start;
}

.customer-ship-dialog__actions {
  background: #f9fafb;
}

.line-height-tight {
  line-height: 1.25;
}

.min-width-0 {
  min-width: 0;
}

.customer-ship-combined-switch >>> .v-input--selection-controls__input {
  margin-right: 8px;
}
</style>
